import './GameLogs.styles.css';

import { observer } from 'mobx-react';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import React, { FC, JSX, useEffect, useMemo, useRef, useState } from 'react';

import { LogEvent, LogsResponse } from '../../../model/LogsResponse';
import commonStore from '../../../store/CommonStore';
import eventLogsStore from '../../../store/EventLogsStore';
import gameStore from '../../../store/GameStore';
import tableStore from '../../../store/TableStore';
import getBlinds from '../../Player/player.render.utils';
import { formatNumber } from '../table.render.utils';
import GameLogsProps from './GameLogs.types';

const GameLogs: FC<GameLogsProps> = ({ tableId, isOpen, isInModal }) => {
  const logsFrame = useRef(null);
  const logsContainer = useRef(null);
  const [eventsLog, setEventsLog] = useState<LogsResponse>({
    tableId: '',
    events: {
      FLOP: [],
      BLINDS: [],
      TURN: [],
      PREFLOP: [],
      RIVER: [],
    },
  });
  const [blindsSum, setBlindsSum] = useState(0);
  const [preflopSum, setPreflopSum] = useState(0);
  const [flopSum, setFlopSum] = useState(0);
  const [turnSum, setTurnSum] = useState(0);

  useEffect(() => {
    if (logsFrame.current && isOpen) {
      eventLogsStore.getLogs(tableId).then(response => {
        setEventsLog(response);

        if (response.events.BLINDS?.length > 0) {
          setBlindsSum(
            Math.max(...response.events.BLINDS.map(event => event.pot)),
          );
        }

        if (response.events.PREFLOP?.length > 0) {
          setPreflopSum(
            Math.max(...response.events.PREFLOP.map(event => event.pot)),
          );
        }

        if (response.events.FLOP?.length > 0) {
          setFlopSum(Math.max(...response.events.FLOP.map(event => event.pot)));
        }

        if (response.events.TURN?.length > 0) {
          setTurnSum(Math.max(...response.events.TURN.map(event => event.pot)));
        }
      });
      if (
        tableStore.viewingTableIds.length > 1 &&
        !tableStore.viewingModalWindowTableId
      ) {
        logsFrame.current.classList.add('show-multi-table');
        logsContainer.current.classList.add('show-logs-container');
      } else logsFrame.current.classList.add('show');
    } else if (logsFrame.current && !isOpen) {
      logsFrame.current.classList.remove('show');
      logsFrame.current.classList.remove('show-multi-table');
      eventLogsStore.removeLogs(tableId);
    }
  }, [
    isOpen,
    eventsLog,
    tableStore.viewingTableIds.length,
    tableStore.viewingTableIds,
    tableStore.viewingModalWindowTableId,
    logsFrame,
    logsContainer,
    eventsLog.events.BLINDS.length,
    eventsLog.events.FLOP.length,
    eventsLog.events.PREFLOP.length,
    eventsLog.events.TURN.length,
    gameStore.reloadGame,
  ]);

  useEffect(() => {
    if (!isOpen && logsContainer.current) {
      setTimeout(
        () => logsContainer.current?.classList?.remove('show-logs-container'),
        1000,
      );
    }
  }, [isOpen, logsContainer]);

  useEffect(() => {
    if (
      isOpen &&
      logsFrame.current &&
      (tableStore.viewingTableIds.length === 1 ||
        tableStore.viewingModalWindowTableId)
    ) {
      setTimeout(
        () =>
          logsFrame.current.scrollIntoView({
            block: 'start',
          }),
        1000,
      );
    }
  }, [
    isOpen,
    logsFrame.current,
    tableStore.viewingModalWindowTableId,
    tableStore.viewingTableIds.length,
  ]);

  const getMoneyValueString = (value: number): string =>
    formatNumber(
      commonStore.convertMoney(
        value,
        tableStore.getActiveTableById(tableId).bb,
      ),
    );

  const logsPerStage = (event: LogEvent, index: number): JSX.Element => {
    return (
      <div key={`${event.pid}-${index}`} className="logs-card">
        <div className="d-flex flex-column justify-content-center align-items-center">
          <Avatar
            image={event.myBot ? '/assets/bot.svg' : '/assets/player.svg'}
          />
          <div className="fs-7 m-1">{getBlinds(event.blinds)}</div>
        </div>
        <div className="log-message-box">
          <div className="pb-2">{event.nick}</div>
          <div className="log-message d-flex flex-column justify-content-center align-items-center">
            <div>
              {event.action.replace('TYPE_', '').replace('STRADDLE', 'ST')}
            </div>
            {event.value ? (
              <div>
                {getMoneyValueString(event.value)}
                {commonStore.getIsChipsConvertedToBlinds() && (
                  <span className="bb-text">{' BB'}</span>
                )}
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  };

  const logsPerBlinds = useMemo(() => {
    return eventsLog?.events.BLINDS?.map(logsPerStage);
  }, [eventsLog?.events.BLINDS, logsPerStage, gameStore.reloadGame]);

  const logsPerPreflop = useMemo(() => {
    return eventsLog?.events.PREFLOP?.map(logsPerStage);
  }, [eventsLog?.events.PREFLOP, logsPerStage, gameStore.reloadGame]);

  const logsPerFlop = useMemo(() => {
    return eventsLog?.events.FLOP?.map(logsPerStage);
  }, [eventsLog?.events.FLOP, logsPerStage, gameStore.reloadGame]);

  const logsPerTurn = useMemo(() => {
    return eventsLog?.events.TURN?.map(logsPerStage);
  }, [eventsLog?.events.TURN, logsPerStage, gameStore.reloadGame]);

  const logsPerRiver = useMemo(() => {
    return eventsLog?.events.RIVER?.map(logsPerStage);
  }, [eventsLog?.events.RIVER, logsPerStage, gameStore.reloadGame]);

  return (
    <div
      ref={logsContainer}
      className={`${isInModal ? 'logs-container-modal' : 'logs-container'}`}
    >
      <div className="logs-close-button">
        {isOpen && (
          <div className="logs-close-button">
            <Button
              icon="pi pi-times"
              rounded
              text
              severity="danger"
              aria-label="Cancel"
              onClick={() => eventLogsStore.toggleLogVisibility(tableId)}
            />
          </div>
        )}
      </div>
      <div ref={logsFrame} className={`logs ${isInModal && 'logs-in-modal'}`}>
        <div className="logs-column">
          <div className="logs-header">Blinds</div>
          <div className="logs-content">{logsPerBlinds}</div>
        </div>
        <div className="logs-column">
          <div className="logs-header">
            Preflop
            <span className="stage-sum">
              {eventsLog?.events.PREFLOP?.length > 0 &&
                getMoneyValueString(blindsSum)}
            </span>
          </div>
          <div className="logs-content">{logsPerPreflop}</div>
        </div>
        <div className="logs-column">
          <div className="logs-header">
            Flop
            <span className="stage-sum">
              {eventsLog?.events.FLOP?.length > 0 &&
                getMoneyValueString(preflopSum)}
            </span>
          </div>
          <div className="logs-content">{logsPerFlop}</div>
        </div>
        <div className="logs-column">
          <div className="logs-header">
            Turn
            <span className="stage-sum">
              {eventsLog?.events.TURN?.length > 0 &&
                getMoneyValueString(flopSum)}
            </span>
          </div>
          <div className="logs-content">{logsPerTurn}</div>
        </div>
        <div className="logs-column">
          <div className="logs-header">
            River
            <span className="stage-sum">
              {eventsLog?.events.RIVER?.length > 0 &&
                (getMoneyValueString(turnSum) || 0)}
            </span>
          </div>
          <div className="logs-content">{logsPerRiver}</div>
        </div>
      </div>
    </div>
  );
};

export default observer(GameLogs);
